import {VuexObjectMixin} from '../../../Mixins';
import Component from 'vue-class-component';
import {Prop} from 'vue-property-decorator';
import {Event, Product, Slot, VisitorSegment} from '../../../Models';
import {CustomerDataDTO} from '../../../DTOs';
import {mapGetters} from 'vuex';
import {VisitorSegmentEnum} from '../../../Enums';
import {AdditionalOrderItemConfiguration} from '../../../Models/AdditionalOrderItemConfiguration';

@Component({
  computed: {
    ...mapGetters({
      visitorSegments: 'getVisitorSegments',
      currentBuyerData: 'getBuyerData',
    }),
  },
})
export default class PublicTransportItem extends VuexObjectMixin {
  @Prop({required: true, type: AdditionalOrderItemConfiguration}) aoic: AdditionalOrderItemConfiguration | undefined;
  @Prop({
    required: false, type: Boolean, default: () => {
      return false;
    },
  }) single: boolean | undefined;

  visitorSegments!: Array<VisitorSegment>;
  currentBuyerData: CustomerDataDTO | undefined;

  product: Product | null = null;

  async mounted() {
    this.product = this.aoic?.getProduct() || null;
    if (this.product === null && this.aoic?.productId) {
      // probably registration view
      this.product = this.products.find(p => p.id === this.aoic?.productId) || null;
    }
    if (this.cart?.order && this.aoic instanceof AdditionalOrderItemConfiguration && !this.aoic.orderItem) {
      this.aoic.orderItem = this.aoic?.getOrderItem(this.cart.order);
    }
    if (!this.canChangeName && this.aoic?.publicTransportConfig) {
      if (this.cart?.order.buyerData?.firstName && this.cart?.order.buyerData?.lastName) {
        this.aoic.publicTransportConfig.passengerSurname = this.cart.order.buyerData.lastName;
        this.aoic.publicTransportConfig.passengerName = this.cart.order.buyerData.firstName;
      } else if (this.currentBuyerData) {
        this.aoic.publicTransportConfig.passengerName = this.currentBuyerData.firstName;
        this.aoic.publicTransportConfig.passengerSurname = this.currentBuyerData.lastName;
      }
    }
  }

  onChangeSelection() {
    this.$emit('updateAdditionalOrderItemConfig', this.aoic);
  }

  get productIsMultipleDays(): boolean {
    return this.productAdmissionDays > 1 || this.isAussteller;
  }

  get productAdmissionDays(): number {
    if (this.product) {
      return this.product.numberFirstDayAdmissions;
    }
    return 1;
  }

  get isValid() {
    if (this.aoic?.publicTransportConfig?.usePubTrans) {
      return this.aoic.publicTransportConfig.selectedDate !== '' &&
        this.aoic.publicTransportConfig.selectedDate !== null &&
        this.aoic.publicTransportConfig.passengerName !== '' &&
        this.aoic.publicTransportConfig.passengerName !== null &&
        this.aoic.publicTransportConfig.passengerSurname !== '' &&
        this.aoic.publicTransportConfig.passengerSurname !== null;
    }
    // if not in use, don't care about the data
    return true;
  }

  get activeVisitorSegments() {
    const segmentIds = this.products.map(p => p.visitorSegmentId);
    return this.visitorSegments.filter(vs => segmentIds.includes(vs.id as number));
  }

  get isAussteller() {
    return this.activeVisitorSegments.find(vs => vs.title === VisitorSegmentEnum.AUSSTELLER) !== undefined;
  }

  get isBesucher() {
    return this.activeVisitorSegments.find(vs => vs.title === VisitorSegmentEnum.BESUCHER) !== undefined;
  }

  get slot(): Slot | null {
    //return this.slots.find(q => q.id === this.pubTransConfig?.slotId);
    return null;
  }

  get event(): Event | null {
    return this.events.find(e => e.id === this.product!.eventId) || null;
  }

  get dateSelection(): Array<Date> {
    const now = new Date();
    now.setHours(0, 0, 0);

    let startDate: Date | null = null;
    let endDate: Date | null = null;

    let eventStart: Date | null = null;
    let eventEnd: Date | null = null;
    let slotStart: Date | null = null;
    let slotEnd: Date | null = null;
    if (this.event) {
      eventStart = new Date(Date.parse(this.event.eventDateStart));
      eventEnd = new Date(Date.parse(this.event.eventDateEnd));
      eventStart.setHours(12, 0, 0);
      eventEnd.setHours(12, 0, 0);
    }
    if (this.slot && this.slot.printStart && this.slot.printEnd) {
      slotStart = new Date(Date.parse(this.slot.printStart));
      slotEnd = new Date(Date.parse(this.slot.printEnd));
      slotStart.setHours(12, 0, 0);
      slotEnd.setHours(12, 0, 0);
    }
    if (this.slot && this.event) {
      startDate = slotStart;
      endDate = slotEnd;
    } else if (this.event) {
      startDate = eventStart;
      endDate = eventEnd;
    }
    if (startDate && startDate < now) {
      startDate = now;
    }
    // the event/slot has already expired, don't allow ticket claim
    if (endDate && endDate < now) {
      endDate = null;
    }
    if (startDate && endDate) {
      let selection: Array<Date> = [];
      for (const dt = new Date(startDate.getTime()); dt <= endDate; dt.setDate(dt.getDate() + 1)) {
        const d = new Date(dt);
        d.setHours(12, 0, 0);
        d.setHours(12, 0, 0);
        selection.push(d);
      }

      return selection;
    }
    return [];
  }

  get slotIsSingleDate() {
    if (this.slot && this.slot.printStart && this.slot.printEnd) {
      const startDate = new Date(Date.parse(this.slot.printStart));
      const endDate = new Date(Date.parse(this.slot.printEnd));
      return startDate.toLocaleDateString() === endDate.toLocaleDateString();
    }
    return false;
  }

  // make sure the slot and the event are compatible with their time ranges
  get slotOutOfRange(): boolean {
    if (!this.slot || !this.event) {
      return false;
    }
    if (this.slot && this.event && this.slot.printStart && this.slot.printEnd) {
      const quotaStart = new Date(Date.parse(this.slot.printStart));
      const quotaEnd = new Date(Date.parse(this.slot.printEnd));
      const eventStart = new Date(Date.parse(this.event.eventDateStart));
      const eventEnd = new Date(Date.parse(this.event.eventDateEnd));
      return (eventStart > quotaEnd || eventEnd < quotaStart);
    }
    return false;
  }

  get isSelected(): boolean {
    return this.aoic?.publicTransportConfig?.usePubTrans === true;
  }

  get configsWithDateSelected(): Array<AdditionalOrderItemConfiguration> {
    let configsWithDateSelected: Array<AdditionalOrderItemConfiguration> = [];
    if (this.aoic?.publicTransportConfig && this.cart?.order.additionalOrderItemConfigurations) {

      this.cart.order.additionalOrderItemConfigurations.forEach((aoic) => {
        if (aoic.publicTransportConfig?.selectedDate && aoic.publicTransportConfig?.selectedDate !== '') {
          configsWithDateSelected.push(aoic);
        }
      });

    }
    return configsWithDateSelected;
  }

  get configsWithPubTransSelected(): Array<AdditionalOrderItemConfiguration> {
    let configsWithPubTransSelected: Array<AdditionalOrderItemConfiguration> = [];
    if (this.aoic?.publicTransportConfig && this.cart?.order.additionalOrderItemConfigurations) {

      this.cart.order.additionalOrderItemConfigurations.forEach((aoic) => {
        if (aoic.publicTransportConfig?.usePubTrans) {
          configsWithPubTransSelected.push(aoic);
        }
      });

    }
    return configsWithPubTransSelected;
  }

  isDateSelectable(date): boolean {
    let isSelectable = true;
    this.configsWithDateSelected.forEach((aoic) => {
      if (aoic.publicTransportConfig && aoic.publicTransportConfig.selectedDate) {
        const selectedDate = new Date(aoic.publicTransportConfig.selectedDate);
        if (aoic.orderItemId === this.aoic?.orderItemId && // Check if same product
          this.product?.isRegistrationRequired && // Only if registration req true - u need to ask marcus why
          selectedDate.toDateString() === new Date(date).toDateString() && // Check if date already selected
          aoic.id !== this.aoic?.id) { // Check if not self
          // TODO?: Could also check for pub-trans-use if needed: && aoic.publicTransportConfig?.usePubTrans
          isSelectable = false;
        }
      }

    });
    return isSelectable;
  }

  get canChangeName(): boolean {
    return !this.product?.isRegistrationRequired;
  }

  get isSubconfig(): boolean {
    return !!this.aoic?.parentOrderItemId;
  }

  get childConfigs(): Array<AdditionalOrderItemConfiguration> {
    let childConfigs: Array<AdditionalOrderItemConfiguration> = [];
    this.cart?.order.additionalOrderItemConfigurations.forEach((aoic) => {
      if (aoic && aoic.parentOrderItemId === this.aoic?.orderItemId) {
        childConfigs.push(aoic);
      }
    });
    return childConfigs;
  }

  get isParentConfig(): boolean {
    return !!this.cart?.order.additionalOrderItemConfigurations.find(
      (aoic) => aoic.parentOrderItemId === this.aoic?.orderItemId,
    );
  }

  get parentConfig(): AdditionalOrderItemConfiguration | null {
    const parentConfig = this.cart?.order.additionalOrderItemConfigurations.find(
      (aoic) => {
        return aoic.orderItemId === this.aoic?.parentOrderItemId;
      },
    );
    return parentConfig ?? null;
  }

  get parentProduct(): Product | null {
    if (this.cart?.order && this.parentConfig) {
      return this.parentConfig.getProduct() ?? null;
    }
    return null;
  }

  get isParentArticleSelected(): boolean {
    if (this.isSubconfig) {
      if (this.parentConfig?.publicTransportConfig) {
        return this.parentConfig.publicTransportConfig.usePubTrans;
      }
    }
    return false;
  }

  get isDisabled(): boolean {
    return (this.isSubconfig && !this.isParentArticleSelected) ||
      (!!this.product?.additionalProduct && !this.atLeastOneTicketWithPriceMoreThanZeroIsSelectedForPublicTransport);
  }

  get atLeastOneTicketWithPriceMoreThanZeroIsSelectedForPublicTransport(): boolean {
    let ticketWithPriceSelected = false;
    this.configsWithPubTransSelected.forEach((aoic) => {
      const product = aoic.getProduct();
      if (product && product.price > 0) {
        ticketWithPriceSelected = true;
      }
    });
    return ticketWithPriceSelected;
  }

  get showPublicTransportItem(): boolean {
    if (this.aoic?.publicTransportConfig) {
      return this.registrationReqAndPersonalUse || (!this.product?.isRegistrationRequired &&
        !!this.product?.localPubTransIncluded);
    }
    return false;
  }

  get registrationReqAndPersonalUse(): boolean {
    if (this.aoic) {
      return (this.product?.isRegistrationRequired || false)
        && !!this.aoic.personalUse;
    }
    return false;
  }

  get productId(): number | null {
    return this.aoic?.productId || null;
  }

  get parentArticleDate(): Date | null {
    // if (this.isSubconfig && this.pubTransConfig && this.pubTransConfig.parentArticlePubTransConfig) {
    //   if (this.pubTransConfig.parentArticlePubTransConfig.date) {
    //     return new Date(this.pubTransConfig.parentArticlePubTransConfig.date);
    //   }
    // }
    return null;
  }

  // TODO: Check for restricted dates here
  // get restrictedDatesForProduct(): Array<RestrictedDates> {
  //   const product = this.product;
  //   if (product) {
  //     return this.restrictedDates.find(c => c.productId === product.id && c.slotId === product.slotId);
  //   }
  //   return [];
  // }
}
